import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { askForStepTimes, isStepTimesRefusal } from '../utils/stepTimes';
import { FEATURE_FLAGS_STORAGE_KEY } from '../constants/storageKeys';
import { unregisterPush } from '../utils/nativePush';
import { API_BASE, BACKEND_URL } from '../config/runtime';

const AuthContext = createContext(null);

// Decided in config/runtime.js: the build-time env in a browser, the chosen
// workspace in the store app.
const API = API_BASE;

/**
 * React 18 runs child useEffects before parent useEffects. Axios interceptors were only
 * registered in AuthProvider's effect, so the first API calls (e.g. purchase invoices) could
 * run without Authorization → 401 and "Failed to load invoices". Install interceptors and
 * sync defaults once at module load (browser only).
 */
function syncAxiosAuthHeaderFromStorage() {
  if (typeof window === 'undefined') return;
  try {
    const t = localStorage.getItem('token');
    if (t) {
      axios.defaults.headers.common.Authorization = `Bearer ${t}`;
    } else {
      delete axios.defaults.headers.common.Authorization;
    }
  } catch {
    /* ignore */
  }
}

function handleAxios401(error) {
  const url = error.config?.url || '';
  if (url.includes('/auth/login')) {
    return;
  }
  try {
    if (window.location.pathname === '/login' || window.location.pathname.endsWith('/login')) {
      return;
    }
    // The WhatsApp form needs no login; a stale token in this browser must not
    // bounce a grader off it to the login screen.
    if (window.location.pathname.startsWith('/wa/')) {
      return;
    }
  } catch {
    /* ignore */
  }
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('isImpersonation');
  localStorage.removeItem(FEATURE_FLAGS_STORAGE_KEY);
  syncAxiosAuthHeaderFromStorage();
  window.location.href = '/login';
}

let _axiosAuthInterceptorsInstalled = false;
function installAxiosAuthInterceptors() {
  if (typeof window === 'undefined' || _axiosAuthInterceptorsInstalled) return;
  _axiosAuthInterceptorsInstalled = true;
  syncAxiosAuthHeaderFromStorage();

  axios.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if ((config.url || '').includes('/auth/login')) {
        const hintedTenant = localStorage.getItem('tenant_id_hint');
        if (hintedTenant) {
          config.headers['X-Tenant-ID'] = hintedTenant;
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      // Silently drop requests cancelled on component unmount
      if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
        return Promise.reject(error);
      }
      if (error.response?.status === 401) {
        handleAxios401(error);
      }
      if (error.response?.status === 409
          && error.response?.data?.detail === 'supervision_hold') {
        announceSupervisionHold(error.response.data);
      }
      if (isStepTimesRefusal(error) && !error.config?._stepTimesRetried) {
        // Ask for the missing step times, then re-send the original request — the
        // page that pressed Submit / Send / Complete just sees it succeed.
        const d = error.response.data.detail;
        return askForStepTimes({
          tankLotId: d.tank_lot_id, productionLotId: d.production_lot_id, lotCode: d.lot_code,
          steps: d.steps, message: d.message, mode: d.mode,
        }).then((saved) => {
          if (saved) return axios({ ...error.config, _stepTimesRetried: true });
          error.response.data = { ...error.response.data, detail: d.message };
          return Promise.reject(error);
        });
      }
      if (isStepTimesRefusal(error)) {
        error.response.data = { ...error.response.data, detail: error.response.data.detail.message };
      }
      return Promise.reject(error);
    }
  );
}

// A held change is announced once, centrally, for the same reason the focus-miss
// handler is: every save in the app can be held, and asking 200 call sites to
// recognise one 409 shape means the ones nobody remembered fail as a bare red
// toast. The page's own catch block still runs — this only adds the dialog.
export const SUPERVISION_HOLD_EVENT = 'supervision:hold';

function announceSupervisionHold(data) {
  try {
    window.dispatchEvent(new CustomEvent(SUPERVISION_HOLD_EVENT, { detail: data }));
  } catch {
    /* a browser that cannot dispatch the event still gets the page's own error */
  }
}

installAxiosAuthInterceptors();

// The feature map from the most recent password login. The login screen reads
// it to decide whether to offer fingerprint sign-in (flag `biometricLogin`) —
// the only moment it knows the tenant's flags before leaving the page.
let lastLoginFeatures = null;
export const getLastLoginFeatures = () => lastLoginFeatures;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // When no token, show login immediately (no loading frame)
  const [loading, setLoading] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      return !!new URLSearchParams(window.location.search).get('impersonation_token') || !!localStorage.getItem('token');
    } catch {
      return false;
    }
  });
  const [isImpersonating, setIsImpersonating] = useState(false);

  // Re-read the identity from the server and update the cached copy in place.
  // Deliberately preserves the local-only fields (`token`, and the impersonation
  // markers set by loginWithToken) — /auth/me does not return them, so a blind
  // overwrite would drop the impersonation banner on the next reload.
  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const { data } = await axios.get(`${API}/auth/me`);
    let merged = null;
    setUser((previous) => {
      merged = {
        ...(previous || {}),
        ...data,
        token,
        tenant_id: data.tenant_id || previous?.tenant_id || localStorage.getItem('tenant_id_hint') || null,
        is_impersonated: previous?.is_impersonated || data.is_impersonated || false,
      };
      try { localStorage.setItem('user', JSON.stringify(merged)); } catch { /* private mode */ }
      return merged;
    });
    return merged;
  }, []);

  useEffect(() => {
    // Check for impersonation token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const impersonationToken = urlParams.get('impersonation_token');
    const tenantHintFromUrl = urlParams.get('tenant_id');
    if (tenantHintFromUrl) {
      localStorage.setItem('tenant_id_hint', tenantHintFromUrl);
    }
    
    if (impersonationToken) {
      handleImpersonationLogin(impersonationToken);
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      const userData = JSON.parse(savedUser);
      setUser(userData);
      setIsImpersonating(userData.is_impersonated || false);
      syncAxiosAuthHeaderFromStorage();
      // Render from the cached copy first so nothing flashes, then reconcile it
      // against the server. Without this the sidebar is built from whatever the
      // role was *at login*: changing somebody's role or stage assignments left
      // them on the old one for the eight hours the token lives, which is a
      // permission change that silently does not take effect. Failures are
      // ignored on purpose — an offline reload must not log anybody out, and the
      // API itself is authorised server-side regardless of what the cache says.
      refreshUser().catch(() => {});
    }
    setLoading(false);
  }, [refreshUser]);

  const handleImpersonationLogin = async (token) => {
    try {
      setLoading(true);
      // Set the token
      localStorage.setItem('token', token);
      localStorage.setItem('isImpersonation', 'true');
      syncAxiosAuthHeaderFromStorage();
      
      // Fetch user info
      const response = await axios.get(`${API}/auth/me`);
      const userData = {
        ...response.data,
        token: token,
        tenant_id: response.data.tenant_id || localStorage.getItem('tenant_id_hint') || null,
        is_impersonated: response.data.is_impersonated || true
      };
      
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      setIsImpersonating(true);
      
      // Force navigation to home page after successful impersonation
      // Use setTimeout to ensure state is saved before navigation
      setTimeout(() => {
        window.location.replace('/');
      }, 100);
    } catch (err) {
      console.error('Impersonation login failed:', err);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('isImpersonation');
      localStorage.removeItem(FEATURE_FLAGS_STORAGE_KEY);
      syncAxiosAuthHeaderFromStorage();
      setLoading(false);
    }
  };

  const loginWithToken = async (token) => {
    try {
      localStorage.setItem('token', token);
      localStorage.setItem('isImpersonation', 'true');
      syncAxiosAuthHeaderFromStorage();

      const response = await axios.get(`${API}/auth/me`);
      const userData = {
        ...response.data,
        token,
        tenant_id: response.data.tenant_id || localStorage.getItem('tenant_id_hint') || null,
        is_impersonated: true,
      };

      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      setIsImpersonating(true);

      // Pass features directly so FeatureFlagContext applies them immediately
      // (no second /auth/me round-trip, no flash of all-tabs-visible).
      // role_features MUST also be passed — FeatureFlagContext's tokenChanged
      // handler short-circuits when d.features is set, so without
      // d.role_features here the sidebar would render with stale (empty)
      // roleFeatures state and the role-matrix gate would fail closed,
      // hiding tabs the super-admin had granted.
      window.dispatchEvent(new CustomEvent('tokenChanged', {
        detail: {
          features: response.data.features || {},
          role_features: response.data.role_features || {},
          tenant_id: response.data.tenant_id,
          lot_number_prefix: response.data.lot_number_prefix,
        },
      }));

      return userData;
    } catch (err) {
      console.error('Token login failed:', err);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('isImpersonation');
      localStorage.removeItem(FEATURE_FLAGS_STORAGE_KEY);
      syncAxiosAuthHeaderFromStorage();
      throw err;
    }
  };

  const login = async (email, password) => {
    const tenantHint = localStorage.getItem('tenant_id_hint');
    const response = await axios.post(
      `${API}/auth/login`,
      { email, password },
      tenantHint ? { headers: { 'X-Tenant-ID': tenantHint } } : undefined
    );
    const { access_token, user: userData, features, role_features, tenant_id, lot_number_prefix } = response.data;
    lastLoginFeatures = features || {};
    const normalizedUser = { ...userData, tenant_id: userData?.tenant_id || tenant_id || null };

    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(normalizedUser));
    localStorage.removeItem('tenant_id_hint');
    localStorage.removeItem('isImpersonation');
    syncAxiosAuthHeaderFromStorage();
    setUser(normalizedUser);
    setIsImpersonating(false);

    // Pass features + role_features in event so FeatureFlagProvider applies
    // them immediately and the sidebar renders with the right role-matrix
    // overrides on first paint. Omitting role_features here caused tabs
    // granted only by the super-admin role matrix to be hidden until the
    // user manually reloaded the page (which then triggered /auth/me).
    window.dispatchEvent(new CustomEvent('tokenChanged', {
      detail: { features, role_features, tenant_id, lot_number_prefix }
    }));

    // Prefetch dashboard so it can be ready when user lands on /.
    // Keep a shared in-flight promise to dedupe immediate duplicate fetches.
    if (typeof window !== 'undefined' && !window.__dashboardPrefetchPromise) {
      window.__dashboardPrefetchPromise = axios
        .get(`${API}/dashboard/overview`)
        .then((r) => {
          window.__dashboardPrefetch = r.data;
          return r.data;
        })
        .catch(() => null)
        .finally(() => {
          window.__dashboardPrefetchPromise = null;
        });
    }

    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    return response.data;
  };

  const logout = () => {
    // Before the token goes: the unregister call has to be authenticated.
    unregisterPush();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('isImpersonation');
    localStorage.removeItem(FEATURE_FLAGS_STORAGE_KEY);
    syncAxiosAuthHeaderFromStorage();
    setUser(null);
    setIsImpersonating(false);
  };

  const endImpersonation = () => {
    logout();
    // Close the tab if opened via impersonation
    if (window.opener) {
      window.close();
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      // Raw JWT for the handful of pages (HR module) that use bare fetch instead
      // of axios. Read from localStorage and keyed off `user` so it re-evaluates
      // on login/logout. Axios-based pages don't need this — the request
      // interceptor in installAxiosAuthInterceptors() attaches the token.
      token: (typeof window !== 'undefined' && user) ? localStorage.getItem('token') : null,
      login,
      loginWithToken,
      register,
      logout,
      loading,
      isImpersonating,
      endImpersonation,
      // Pull the identity again without a re-login. The admin screen calls this
      // after editing somebody's own role or stage assignments, so the change
      // shows in their sidebar immediately instead of at their next login.
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export { API, BACKEND_URL };
